import React, { useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Robot, ArrowRight, EnvelopeSimple, CheckCircle, Copy } from "@phosphor-icons/react";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      const { data } = await api.post("/auth/forgot-password", { email: email.trim().toLowerCase() });
      setResult(data);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  const copy = () => {
    if (result?.reset_url) {
      navigator.clipboard.writeText(result.reset_url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-8 bg-white">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-2 mb-8" data-testid="brand-link">
          <div className="w-9 h-9 bg-[var(--ink)] flex items-center justify-center">
            <Robot size={20} weight="duotone" color="#FFD600" />
          </div>
          <span className="font-display text-2xl">Robocope</span>
        </Link>

        <div className="overline text-[var(--ink-soft)]">/ Reset access</div>
        <h1 className="font-display text-4xl tracking-tight mt-2 mb-2">Forgot your password?</h1>
        <p className="text-[var(--ink-soft)] mb-8">Drop your email and we'll generate a reset link.</p>

        {!result ? (
          <form onSubmit={submit} className="space-y-4" data-testid="forgot-form">
            <label className="block">
              <div className="overline text-[var(--ink-soft)] mb-2">Email</div>
              <div className="relative">
                <EnvelopeSimple size={18} weight="duotone" className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--ink-soft)]" />
                <input
                  type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.com" className="input-rc pl-10"
                  data-testid="forgot-email-input"
                />
              </div>
            </label>
            {error && <div className="cell p-3 text-sm bg-red-50 text-red-700 border-red-200" data-testid="forgot-error">{error}</div>}
            <button type="submit" disabled={busy} className="btn-primary w-full justify-center" data-testid="forgot-submit-btn">
              {busy ? "Working…" : "Send reset link"} <ArrowRight size={18} weight="bold" />
            </button>
          </form>
        ) : (
          <div className="space-y-4" data-testid="forgot-success">
            <div className="cell-strong bg-[var(--accent)] p-5">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle size={22} weight="fill" />
                <div className="font-display text-xl">Reset link generated</div>
              </div>
              <p className="text-sm">If <strong>{email}</strong> is registered, a reset link has been created and is valid for 60 minutes.</p>
            </div>
            {result.reset_url && (
              <div className="cell-strong bg-[var(--ink)] text-white p-5" data-testid="reset-url-block">
                <div className="overline text-white/60 mb-2">/ V1 — Email delivery not wired yet</div>
                <p className="text-sm text-white/70 mb-3">
                  Email sending (SendGrid / Resend) is on the V2 roadmap. Until then we surface the reset link here for you.
                </p>
                <div className="bg-black/40 p-3 font-mono-rc text-xs break-all border border-white/10 mb-3" data-testid="reset-url">
                  {result.reset_url}
                </div>
                <div className="flex gap-2 flex-wrap">
                  <button onClick={copy} className="btn-accent text-sm" data-testid="copy-reset-btn">
                    <Copy size={16} weight="bold" /> {copied ? "Copied!" : "Copy link"}
                  </button>
                  <Link to={result.reset_url.split(window.location.origin)[1] || "/"} className="btn-secondary text-sm bg-white" data-testid="open-reset-link">
                    Open link <ArrowRight size={16} weight="bold" />
                  </Link>
                </div>
              </div>
            )}
            <Link to="/login" className="text-sm underline" data-testid="back-to-login">← Back to log in</Link>
          </div>
        )}

        {!result && (
          <div className="mt-6 text-sm text-[var(--ink-soft)]">
            Remembered it? <Link to="/login" className="font-medium underline" data-testid="link-login">Log in</Link>
          </div>
        )}
      </div>
    </div>
  );
}
